import type { ShowConsultation } from '@/components/PatientPageV2/components/ConsultationTabSections/ShowConsultation';
import { useConsultationTabState } from '@/components/PatientPageV2/hooks/useConsultationTabState';
import type { SaveConsultationOptions } from '@/hooks/consult/useSaveConsultation';
import { useSaveConsultation } from '@/hooks/consult/useSaveConsultation';
import type { SaveConsultationParams } from '@/hooks/consult/useSaveConsultationData';
import { useAppStore } from '@/state-management';
import { useCallback, useState } from 'react';

interface useSaveScheduledConsultProps {
  consultation: ShowConsultation | null;
  doctorId: number;
}

export const useSaveScheduledConsult = ({ consultation, doctorId }: useSaveScheduledConsultProps) => {
  const store = useAppStore();
  const patientId = store.getPatientId();
  const [savingConsult, setSavingConsult] = useState(false);

  const onSaveComplete = useCallback(async () => {
    store.setIsDirty(false);
  }, [store]);

  const { saveConsultation } = useSaveConsultation({
    consultation,
    shouldUpdateConsultationStatus: true,
    onRefresh: () => {},
    onSaveComplete,
  });

  const { submitPrescriptions, isPostingPrescription, prescribeStateError } = useConsultationTabState({
    doctorId,
    patientId: patientId!,
    showConsultation: consultation,
  });

  const saveConsultationWithState = useCallback(
    async (
      consultationData: SaveConsultationParams | null,
      options?: Omit<SaveConsultationOptions, 'closeConsultation' | 'enableCustomOnSave'>,
    ) => {
      setSavingConsult(true);
      await saveConsultation(consultationData, options);
      setSavingConsult(false);
    },
    [saveConsultation],
  );

  return {
    saveConsult: saveConsultationWithState,
    submitCreatePrescription: submitPrescriptions,
    isPostingPrescription,
    prescribeStateError,
    savingConsult,
  };
};
